


import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container, 
  Paper, 
  CircularProgress,
  useTheme,
  useMediaQuery,
  Link
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@mui/icons-material/School';
import AOS from 'aos';
import 'aos/dist/aos.css';

const InstitutionLogin = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [email, setEmail] = useState('');
  const [schoolCode, setSchoolCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [requiresVerification, setRequiresVerification] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Text content
  const cbcText = 'With Competency Based Education Implemented Technologically';
  const cbcTextShort = '(CBE)';

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (requiresVerification) {
      if (!verificationCode.trim()) {
        setError('Please enter the verification code');
        return;
      }
    } else {
      if (!email.trim() || !schoolCode.trim()) {
        setError('Please fill in all fields');
        return;
      }
    }

    try {
      let result;
      if (requiresVerification) {
        result = await auth.schoolLogin(email, schoolCode, verificationCode);
      } else {
        result = await auth.schoolLogin(email, schoolCode);
      }
      
      if (result?.requiresVerification) {
        setRequiresVerification(true);
      } else if (result) {
        navigate('/', {
          state: {
            schoolName: result.schoolName,
            schoolId: result.schoolId
          }
        });
      }
    } catch (error) {
      setError(error.message || 'Login failed. Please try again.');
    }
  };

  const handleResendCode = async () => {
    try {
      const success = await auth.resendVerificationCode(email, schoolCode);
      if (success) {
        setVerificationCode('');
        setError('');
      }
    } catch (error) {
      setError(error.message || 'Failed to resend code. Please try again.');
    }
  };

  const handleBackToLogin = () => {
    setRequiresVerification(false);
    setVerificationCode('');
    setError('');
  };

  // Character animation variants
  const charVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.5 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      color: `hsl(${(i * 10) % 360}, 100%, 50%)`,
      transition: {
        delay: i * 0.05,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  return (
    <Container 
      maxWidth="sm" 
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e1e5eb 100%)',
        p: 2
      }}
    >
      {/* Animated Welcome Section */}
      <Box 
        sx={{ 
          textAlign: 'center', 
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
        data-aos="fade-down"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            width: 120,
            height: 120,
            borderRadius: 60,
            backgroundColor: '#6200ee',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 16,
            boxShadow: '0 4px 20px rgba(98, 0, 238, 0.3)'
          }}
        >
          <Icon 
            sx={{ 
              fontSize: 48, 
              color: 'white',
              animation: 'pulse 2s infinite'
            }} 
          />
        </motion.div>

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            sx={{
              fontWeight: 'bold',
              mb: 3,
              background: 'linear-gradient(to right, #6200ee, #03dac6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {requiresVerification ? 'Verify Your Email' : 'Welcome Back, School!'}
          </Typography>
        </motion.div>
      </Box>

      {/* Animated CBC Text */}
      {!requiresVerification && (
        <Box 
          sx={{ 
            mb: 4,
            textAlign: 'center'
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              flexWrap: 'wrap',
              mb: 1
            }}
          >
            {cbcText.split('').map((char, index) => (
              <motion.span
                key={`long-${index}`}
                custom={index}
                initial="hidden"
                animate="visible"
                variants={charVariants}
                style={{
                  fontWeight: 'bold',
                  margin: '0 1px',
                  fontSize: isMobile ? 14 : 16
                }}
              >
                {char}
              </motion.span>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
            {cbcTextShort.split('').map((char, index) => (
              <motion.span
                key={`short-${index}`}
                custom={index + cbcText.length}
                initial="hidden"
                animate="visible"
                variants={charVariants}
                style={{
                  fontWeight: 'bold',
                  margin: '0 1px',
                  fontSize: isMobile ? 18 : 22
                }}
              >
                {char}
              </motion.span>
            ))}
          </Box>
        </Box>
      )}

      {/* Login Form */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
          data-aos="zoom-in"
        >
          {error && (
            <motion.div
              initial={{ x: -10 }}
              animate={{ x: 0 }}
              transition={{ type: 'spring', stiffness: 500 }}
            >
              <Typography 
                color="error" 
                align="center" 
                sx={{ 
                  mb: 2,
                  fontWeight: 500
                }}
              >
                {error}
              </Typography>
            </motion.div>
          )}

          {requiresVerification ? (
            <>
              <Typography 
                align="center" 
                sx={{ 
                  mb: 3,
                  color: 'text.secondary'
                }}
              >
                We've sent a verification code to <strong>{email}</strong>
              </Typography>
              
              <TextField
                fullWidth
                label="Verification Code"
                variant="outlined"
                margin="normal"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                disabled={auth.isLoading}
                inputProps={{
                  maxLength: 6,
                  style: {
                    textTransform: 'uppercase',
                    letterSpacing: '0.5em',
                    textAlign: 'center',
                    fontSize: '1.2rem'
                  }
                }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#ddd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#6200ee',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6200ee',
                    },
                  },
                }}
              />

              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Link 
                  component="button" 
                  variant="body2"
                  onClick={handleResendCode}
                  disabled={auth.isLoading}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Resend Verification Code
                </Link>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={handleBackToLogin}
                  disabled={auth.isLoading}
                  sx={{ 
                    height: '48px',
                    borderRadius: 2,
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    textTransform: 'none',
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      backgroundColor: 'rgba(98, 0, 238, 0.04)'
                    }
                  }}
                >
                  Back
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleLogin}
                  disabled={auth.isLoading}
                  sx={{ 
                    height: '48px',
                    borderRadius: 2,
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    textTransform: 'none',
                    boxShadow: '0 4px 6px rgba(98, 0, 238, 0.2)',
                    '&:hover': {
                      boxShadow: '0 6px 8px rgba(98, 0, 238, 0.3)',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {auth.isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Verify'
                  )}
                </Button>
              </Box>
            </>
          ) : (
            <Box component="form" onSubmit={handleLogin} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={auth.isLoading}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#ddd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#6200ee',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6200ee',
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="School Code"
                variant="outlined"
                margin="normal"
                type="password"
                value={schoolCode}
                onChange={(e) => setSchoolCode(e.target.value)}
                disabled={auth.isLoading}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '& fieldset': {
                      borderColor: '#ddd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#6200ee',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6200ee',
                    },
                  },
                }}
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                disabled={auth.isLoading}
                sx={{ 
                  mt: 1, 
                  mb: 2, 
                  height: '48px',
                  borderRadius: 2,
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 6px rgba(98, 0, 238, 0.2)',
                  '&:hover': {
                    boxShadow: '0 6px 8px rgba(98, 0, 238, 0.3)',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {auth.isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Login'
                )}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => navigate('/signup')}
                disabled={auth.isLoading}
                sx={{ 
                  mt: 1,
                  height: '48px',
                  borderRadius: 2,
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    backgroundColor: 'rgba(98, 0, 238, 0.04)'
                  }
                }}
              >
                Sign Up
              </Button>
            </Box>
          )}
        </Paper>
      </motion.div>

      {/* Footer */}
      <Box 
        sx={{ 
          mt: 4,
          textAlign: 'center'
        }}
      >
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'text.secondary',
            fontWeight: 500
          }}
        >
          Powered by Fusion-Xe
        </Typography>
      </Box>
    </Container>
  );
};

export default InstitutionLogin;